import { Info } from 'lucide-react'
import { useState } from 'react'
import { KpiTile, Note, Panel } from '@/components/redesign/bits'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { formatDate, numberFmt } from '@/lib/format'
import { type ConnectorRange, useConnectorOverview } from './api'
import { CallsTable } from './calls-table'

const rangeLabels: Record<ConnectorRange, string> = { '7d': '7 days', '30d': '30 days', '90d': '90 days' }

/**
 * What people do with Arva SEO from connected AI assistants: the tools they use, how far they get
 * and where they get stuck. From the calls the connector records; never their prompts.
 */
export function AiConnectorPage() {
  const [range, setRange] = useState<ConnectorRange>('30d')
  const overview = useConnectorOverview(range)

  return (
    <>
      <PageTitle
        title="AI connector"
        description="What customers do with Arva SEO from connected AI assistants: the tools they use, how far they get and where they get stuck. Their prompts and saved content aren’t recorded."
        actions={
          <Segmented<ConnectorRange>
            label="Period"
            value={range}
            onChange={setRange}
            options={(Object.keys(rangeLabels) as ConnectorRange[]).map((r) => ({
              value: r,
              label: rangeLabels[r],
            }))}
          />
        }
      />
      {overview.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : overview.isError ? (
        <div className="card">
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        </div>
      ) : (
        <>
          <div className="kpis">
            <KpiTile
              label="Calls"
              value={numberFmt.format(overview.data.totals.calls)}
              compare={`in the last ${rangeLabels[range]}`}
            />
            <KpiTile
              label="People"
              value={numberFmt.format(overview.data.totals.people)}
              compare="who used it"
            />
            <KpiTile
              label="Workspaces"
              value={numberFmt.format(overview.data.totals.workspaces)}
              compare="that used it"
            />
            <KpiTile
              label="AI assistants"
              value={overview.data.apps.map((a) => numberFmt.format(a.people)).join(' / ') || '0'}
              compare={overview.data.apps.map((a) => a.app).join(' / ') || 'nobody yet'}
            />
          </div>
          {overview.data.tracking_since && (
            <Note
              calm
              icon={<Info aria-hidden="true" />}
              title={`Recorded since ${formatDate(overview.data.tracking_since)}`}
            >
              Calls before then weren’t recorded, so earlier use doesn’t show here.
            </Note>
          )}

          <div className="grid2">
            <Panel title="How far they get" sub="Everyone who ever connected an AI assistant">
              {overview.data.funnel.map((f) => (
                <div className="srow" key={f.key}>
                  <div>
                    <b>{f.label}</b>
                  </div>
                  <b className="num">{numberFmt.format(f.people)}</b>
                </div>
              ))}
            </Panel>
            <Panel title="Where they get stuck" sub="Calls that found nothing or were refused">
              {overview.data.stuck.length === 0 ? (
                <p className="empty-line">Nothing in this period.</p>
              ) : (
                overview.data.stuck.map((s) => (
                  <div className="srow" key={s.reason ?? 'other'}>
                    <div>
                      <b>{s.label}</b>
                      <small>
                        {numberFmt.format(s.people)} {s.people === 1 ? 'person' : 'people'}
                      </small>
                    </div>
                    <b className="num">{numberFmt.format(s.calls)}</b>
                  </div>
                ))
              )}
            </Panel>
          </div>

          <Panel title="Tools used" sub="Most used first">
            {overview.data.tools.length === 0 ? (
              <p className="empty-line">No calls in this period.</p>
            ) : (
              <div className="tw" style={{ border: 0 }}>
                <table className="t" style={{ minWidth: 520 }}>
                  <caption className="sr-only">Tools used</caption>
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th className="r">Calls</th>
                      <th className="r">People</th>
                      <th className="r">Found nothing</th>
                      <th className="r">Refused</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.data.tools.map((t) => (
                      <tr key={t.tool}>
                        <td>{t.label}</td>
                        <td className="r num">{numberFmt.format(t.calls)}</td>
                        <td className="r num">{numberFmt.format(t.people)}</td>
                        <td className="r num">{numberFmt.format(t.empty)}</td>
                        <td className="r num">{numberFmt.format(t.refused)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Latest calls" sub="The 50 most recent">
            <CallsTable calls={overview.data.recent} />
          </Panel>
        </>
      )}
    </>
  )
}
